import {CustomFieldDateComponent} from "module/CustomField/component/input/CustomFieldDateComponent";
import {convertCFInputTypeToFileMode, CustomFieldFileComponent} from "module/CustomField/component/input/CustomFieldFileComponent";
import {CustomFieldSelectComponent} from "module/CustomField/component/input/CustomFieldSelectComponent";
import {CustomFieldSignatureComponent} from "module/CustomField/component/input/CustomFieldSignatureComponent";
import {JaNeinCheckboxComponent} from 'module/CustomField/component/input/JaNeinCheckboxComponent';
import {JaNeinNullComponent} from "module/CustomField/component/input/JaNeinNullComponent";
import {JaNeinRadioComponent} from 'module/CustomField/component/input/JaNeinRadioComponent';
import {InputType} from "module/CustomField/util/CFInputType";
import React from 'react';
import 'module/CustomField/style/CustomFieldBorderCSS'
import {CustomFieldTextareaComponent} from 'module/CustomField/component/input/CustomFieldTextareaComponent';
import {CFMember, CFValue, isCFMember} from 'module/CustomField/type/CFType';
import {CFComponentLazyProps, CFComponentProps} from 'module/CustomField/type/CFComponentType';
import {DqRadioComponent} from 'module/CustomField/component/input/DqRadioComponent'
import {CustomFieldNumberComponent} from 'module/CustomField/component/input/CustomFieldNumberComponent'
import {CustomFieldFileLinkComponent} from 'module/CustomField/component/input/CustomFieldFileLinkComponent'
import {CustomFieldGPSLeafComponent} from 'module/CustomField/component/input/CustomFieldGPSLeafComponent'
import {JaNeinToggleComponent} from 'module/CustomField/component/input/JaNeinToggleComponent'
import {log} from 'common/log'
import {CustomFieldSelectMultiComponent} from 'module/CustomField/component/input/CustomFieldSelectMultiComponent'
import {CustomFieldDurationAsTimeToComponent} from 'module/CustomField/component/input/CustomFieldDurationAsTimeToComponent'
import {CustomFieldKostSelectComponent} from 'module/CustomField/component/input/CustomFieldKostSelectComponent'
import {CustomFieldDqLabelComponent} from 'module/CustomField/component/input/CustomFieldDqLabelComponent'
import {CustomFieldEntryDescriptionMultiComponent} from 'module/CustomField/component/input/CustomFieldEntryDescriptionMultiComponent'
import {CustomFieldPasswordComponent} from "./input/CustomFieldPasswordComponent";
import {CustomFieldLinkButtonComponent} from 'module/CustomField/component/input/CustomFieldLinkButtonComponent'
import {CustomFieldAvailableInputTypesComponent} from 'module/CustomField/component/input/CustomFieldAvailableInputTypesComponent'
import {CustomFieldHexColorComponent} from 'module/CustomField/component/input/CustomFieldHexColorComponent'
import {CustomFieldRawInputComponent} from 'module/CustomField/component/input/CustomFieldRawInputComponent'
import {CustomFieldVerboseInputComponent} from 'module/CustomField/component/input/CustomFieldVerboseInputComponent'
import {CustomFieldKostkeySelectComponent} from 'module/CustomField/component/input/CustomFieldKostkeySelectComponent'
import {CustomFieldLabelComponent} from 'module/CustomField/component/input/CustomFieldLabelComponent'
import {TextAlign} from 'type/TextAlign'
import {CustomFieldPersonSelectComponent} from 'module/CustomField/component/input/CustomFieldPersonSelectComponent'

function transformLazyBlobProps<V extends CFMember>(props: CFComponentProps<V>): CFComponentLazyProps {
	let {disabled, modelValue, resetCb, ...otherProps} = props
	if (modelValue && !isCFMember(modelValue)) throw new Error('not a blob-cfdef')
	let {data, mimeType, filename, preview, lazy, version} = modelValue || {} as V
	const loading = lazy && !preview && !data
	const lazyProps = {
		data,
		preview: preview !== '_nopreview_' ? preview : undefined,
		mimeType,
		filename,
		version,
		loading,
		disabled: Boolean(disabled || loading),
		resetCb,
		hasData: Boolean(lazy || data || preview),
	}
	return {...otherProps, ...lazyProps}
}

const _alreadyWarned: { [k in string]?: boolean } = {'': true}

export function CustomFieldComponent<V extends CFMember | CFValue>(props: CFComponentProps<V>) {
	const {cfDef} = props;
	let cfComponent = null;

	if (!InputType[cfDef.input as InputType]) {
		if (!_alreadyWarned[cfDef.input]) {
			log.warn('Invalid input type: ', cfDef.input);
			_alreadyWarned[cfDef.input] = true;
		}
	}

	switch (cfDef.input as InputType | null) {
		case InputType.SisDate:
		case InputType.Date:
			cfComponent = <CustomFieldDateComponent {...props as CFComponentProps<number>}/>;
			break;
		case InputType.DurationAsTimeTo:
			cfComponent = <CustomFieldDurationAsTimeToComponent {...props as CFComponentProps<string>}/>
			break;
		case InputType.JaNeinNull:
			cfComponent = <JaNeinNullComponent {...props as CFComponentProps<'J' | 'N' | null>}/>;
			break;
		case InputType.JaNeinCheckbox:
			cfComponent = <JaNeinCheckboxComponent {...props as CFComponentProps<'J' | 'N'>}/>;
			break;
		case InputType.JaNeinToggle:
			cfComponent = <JaNeinToggleComponent {...props as CFComponentProps<'J' | 'N'>}/>;
			break;
		case InputType.JaNeinRadio:
			cfComponent = <JaNeinRadioComponent {...props as CFComponentProps<'J' | 'N'>}/>;
			break;
		case InputType.File:
		case InputType.Image:
		case InputType.Photo:
		case InputType.Document:
		case InputType.DocumentOrImage:
		case InputType.Video:
			cfComponent = <CustomFieldFileComponent modes={convertCFInputTypeToFileMode(cfDef.input)} {...transformLazyBlobProps(props as CFComponentProps<CFMember>)}/>;
			break;
		case InputType.FileLink:
			cfComponent = <CustomFieldFileLinkComponent {...props as CFComponentProps<CFMember>}/>;
			break;
		case InputType.Signature:
			cfComponent = <CustomFieldSignatureComponent {...transformLazyBlobProps(props as CFComponentProps<CFMember>)}/>;
			break;
		case InputType.GPS:
			cfComponent = <CustomFieldGPSLeafComponent {...props as CFComponentProps<CFMember>} />
			break;
		case InputType.Label:
			cfComponent = <CustomFieldLabelComponent {...props as CFComponentProps<CFValue | CFMember>}/>
			break;
		case InputType.LinkButton:
			cfComponent = <CustomFieldLinkButtonComponent {...props as CFComponentProps<CFValue>}/>
			break;
		case InputType.Textarea:
			cfComponent = <CustomFieldTextareaComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.DqRadio:
			cfComponent = <DqRadioComponent {...props as CFComponentProps<CFValue>}/>
			break;
		case InputType.Number:
		case InputType.Integer:
			cfComponent = <CustomFieldNumberComponent {...props as CFComponentProps<CFValue>} />
			break;
		case InputType.DqMulti:
			cfComponent = <CustomFieldSelectMultiComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.EntryDescriptionMulti:
			cfComponent = <CustomFieldEntryDescriptionMultiComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.Kost:
		case InputType.KBerschl:
			cfComponent = <CustomFieldKostSelectComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.Person:
			cfComponent = <CustomFieldPersonSelectComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.Kostkey:
			cfComponent = <CustomFieldKostkeySelectComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.DqLabel:
			cfComponent = <CustomFieldDqLabelComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.Password:
			cfComponent = <CustomFieldPasswordComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.AlignRight:
		case InputType.Id:
			cfComponent = <CustomFieldSelectComponent {...props as CFComponentProps<CFValue>} align={TextAlign.right}/>;
			break;
		case InputType.AlignCenter:
			cfComponent = <CustomFieldSelectComponent {...props as CFComponentProps<CFValue>} align={TextAlign.center}/>;
			break;
		case InputType.CustomFieldAvailableInputTypes:
			cfComponent = <CustomFieldAvailableInputTypesComponent {...props as CFComponentProps<CFValue>}/>;
			break;
		case InputType.HexColor:
			cfComponent = <CustomFieldHexColorComponent {...props as CFComponentProps<CFValue>}/>
			break;
		case InputType.Raw:
			cfComponent = <CustomFieldRawInputComponent {...props as CFComponentProps<CFValue>}/>
			break;
		case InputType.Verbose:
			cfComponent = <CustomFieldVerboseInputComponent {...props as CFComponentProps<CFValue>}/>
			break;
		default:
		case InputType.Time:
		case InputType.Duration:
		case InputType.Dq:
		case InputType.Key:
		case InputType.String:
		case InputType.Uri:
			cfComponent = <CustomFieldSelectComponent {...props as CFComponentProps<CFValue>}/>;
			break;
	}
	return cfComponent
}